import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, Clock, MessageCircle, Phone } from "lucide-react";
import SEO, { breadcrumbLd } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/pages/Home";
import { BUSINESS, SITE_URL, WA_DEFAULT, WA_ORDER } from "@/data/business";
import { ARTICLES, getArticle } from "@/data/blog";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound />;

  const crumb = [
    { name: "الرئيسية", path: "/" },
    { name: "المدونة", path: "/المدونة" },
    { name: article.title, path: `/المدونة/${article.slug}` },
  ];
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);
  const dateFmt = new Date(article.date).toLocaleDateString("ar-SA-u-nu-latn", { year: "numeric", month: "long", day: "numeric" });

  return (
    <>
      <SEO
        title={`${article.title} | مدونة نجم الإطار`}
        description={article.excerpt}
        path={`/المدونة/${article.slug}`}
        image={article.image}
        type="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            inLanguage: "ar",
            datePublished: article.date,
            author: { "@type": "Organization", name: "بنشر متنقل نجم الإطار" },
            publisher: { "@id": `${SITE_URL}/#business` },
            mainEntityOfPage: `${SITE_URL}/المدونة/${encodeURIComponent(article.slug)}`,
          },
          breadcrumbLd(crumb),
        ]}
      />

      <article className="pt-28">
        <header className="max-w-3xl mx-auto px-4 sm:px-6">
          <nav aria-label="مسار التنقل" className="text-xs text-gray-500 mb-5 flex items-center gap-1.5 flex-wrap" data-testid="post-breadcrumb">
            <Link to="/" className="hover:text-gold-soft">الرئيسية</Link>
            <ChevronLeft size={12} />
            <Link to="/المدونة" className="hover:text-gold-soft">المدونة</Link>
            <ChevronLeft size={12} />
            <span className="text-gold-soft">{article.title}</span>
          </nav>
          <h1 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="post-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 mt-5 text-xs text-gray-500">
            <span data-testid="post-date">{dateFmt}</span>
            <span className="flex items-center gap-1.5"><Clock size={13} /> {article.readTime}</span>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-10">
          <div className="rounded-3xl overflow-hidden border border-edge">
            <img src={article.image} alt={article.alt} fetchPriority="high" className="w-full max-h-[420px] object-cover" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          {article.sections.map((sec, i) => (
            <Reveal key={sec.h2} className="mb-10">
              <section>
                <h2 className="font-display font-bold text-white text-xl sm:text-2xl mb-4 flex items-center gap-3">
                  <span className="h-6 w-1.5 rounded-full bg-gold shrink-0" />
                  {sec.h2}
                </h2>
                <p className="text-gray-300 leading-loose">{sec.body}</p>
                {sec.list && (
                  <ul className="mt-4 space-y-2.5">
                    {sec.list.map((li, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed">
                        <CheckCircle2 size={16} className="text-gold shrink-0 mt-0.5" />
                        {li}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Reveal>
          ))}

          <Reveal>
            <div className="bg-gold/10 border border-gold/40 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display font-black text-white text-xl mb-3">الخلاصة</h2>
              <p className="text-gray-200 leading-loose text-sm sm:text-base">{article.conclusion}</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <a href={WA_ORDER} target="_blank" rel="noopener noreferrer" data-testid="post-order-cta" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-ink font-black rounded-full px-6 py-3 transition-colors text-sm">
                  <MessageCircle size={15} /> اطلب بنشر متنقل عبر واتساب
                </a>
                <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid="post-whatsapp-cta" className="inline-flex items-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-6 py-3 transition-colors text-sm">
                  <MessageCircle size={15} /> واتساب مباشر
                </a>
                <a href={BUSINESS.telLink} data-testid="post-call-cta" className="inline-flex items-center gap-2 border border-gold/50 text-gold-soft hover:bg-gold hover:text-ink font-bold rounded-full px-6 py-3 transition-colors text-sm">
                  <Phone size={15} /> <span dir="ltr">{BUSINESS.phoneDisplay}</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </article>

      <section className="bg-panel border-t border-edge py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-display font-bold text-white text-2xl mb-8">مقالات ذات صلة</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/المدونة/${a.slug}`}
                data-testid={`post-related-${a.slug}`}
                className="group bg-ink border border-edge hover:border-gold/50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={a.image} alt={a.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-white text-base leading-snug group-hover:text-gold-soft transition-colors">{a.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="سؤالك لم تجد جوابه؟ اسأل الفني مباشرة" testid="post-cta-band" />
    </>
  );
}
